// src/Studio/timeline/fxRuntime.targeting.ts
//
// Task T6 of the 2026-04-08 studio file-splits plan. Pure mechanical
// extraction from fxRuntime.ts — no behavior change.
//
// Owns:
//   - FX_ACTIVE_EPS
//   - Clip-lookup memoization (_fxClipLookupRef / _fxClipLookup)
//   - getFxClipsByIdCached, dedupeFxBlocksById
//   - getTargetElementIds, isPlayheadWithinFxWindow
//   - packetEffectTypeIsBackgroundBlur, packetIsBackgroundBlur
//   - packetWantsSegmentation, selectBackgroundBlurPacketForOverlay

import type { Clip } from "./EditorStore";
import type { FxBlock } from "./fxTypes";
import type { FxRenderPacket } from "./fxRuntime";
import { resolveFxRuntimeProfile } from "./fxEffectCatalog";

export const FX_ACTIVE_EPS = 1 / 240;

// ── clip-lookup memoization ──────────────────────────────────────

let _fxClipLookupRef: Clip[] | null = null;
let _fxClipLookup = new Map<string, Clip>();

export function getFxClipsByIdCached(clips: Clip[]): Map<string, Clip> {
  if (clips === _fxClipLookupRef) return _fxClipLookup;
  _fxClipLookupRef = clips;
  _fxClipLookup = new Map(clips.map((c) => [c.id, c]));
  return _fxClipLookup;
}

export function dedupeFxBlocksById(fxBlocks: FxBlock[]): FxBlock[] {
  if (!Array.isArray(fxBlocks) || fxBlocks.length <= 1) return fxBlocks;
  const byId = new Map<string, FxBlock>();
  for (const block of fxBlocks) {
    if (!block?.id) continue;
    if (byId.has(block.id)) byId.delete(block.id);
    byId.set(block.id, block);
  }
  return [...byId.values()];
}

// ── targeting helpers ────────────────────────────────────────────

export function isPlayheadWithinFxWindow(
  playhead: number,
  start: number,
  end: number,
): boolean {
  return playhead + FX_ACTIVE_EPS >= start && playhead <= end + FX_ACTIVE_EPS;
}

export function packetEffectTypeIsBackgroundBlur(effectType: string): boolean {
  const rawEffect = String(effectType || "").trim().toLowerCase();
  const fxType = resolveFxRuntimeProfile(effectType);
  return (
    fxType === "background-blur-only" ||
    fxType === "background-blur" ||
    rawEffect === "background-blur-only" ||
    rawEffect === "background-blur" ||
    rawEffect.startsWith("background-blur")
  );
}

export function getTargetElementIds(
  block: FxBlock,
  clips: Clip[],
  playhead: number,
  clipById?: Map<string, Clip>,
): Set<string> {
  const ids = new Set<string>();
  if (!clipById) clipById = getFxClipsByIdCached(clips);
  const explicitClipTargets = Array.isArray(block.targetClipIds)
    ? block.targetClipIds
    : [];
  const explicitTrackTargets = Array.isArray(block.targetTrackIds)
    ? block.targetTrackIds
    : [];
  const hasExplicitClipTargets = explicitClipTargets.length > 0;
  const hasExplicitTrackTargets = explicitTrackTargets.length > 0;
  const hasExplicitTargets = hasExplicitClipTargets || hasExplicitTrackTargets;

  if (hasExplicitClipTargets) {
    for (const clipId of explicitClipTargets) {
      const clip = clipById.get(clipId);
      if (clip?.elementId) ids.add(clip.elementId);
    }
  }

  if (hasExplicitTrackTargets) {
    for (const clip of clips) {
      if (explicitTrackTargets.includes(clip.trackId) && clip.elementId) {
        ids.add(clip.elementId);
      }
    }
  }

  let hasVisibleExplicitTarget = false;
  if (hasExplicitTargets && ids.size > 0) {
    for (const clip of clips) {
      if (!clip.elementId || !ids.has(clip.elementId)) continue;
      if (isPlayheadWithinFxWindow(playhead, clip.start, clip.end)) {
        hasVisibleExplicitTarget = true;
        break;
      }
    }
  }
  const isBackgroundBlurBlock = packetEffectTypeIsBackgroundBlur(
    block.effectType,
  );

  // UX fallback:
  // - no explicit targets: apply to clips currently visible at playhead
  // - explicit clip targets that no longer resolve (e.g. stale IDs after restore): fail soft to visible clips
  // - background blur with explicit targets that are not visible at current playhead:
  //   fail soft to visible clips so effect remains continuous for the full FX block span.
  const shouldFallbackToVisible =
    !hasExplicitTargets ||
    (hasExplicitClipTargets && ids.size === 0) ||
    (isBackgroundBlurBlock && hasExplicitTargets && !hasVisibleExplicitTarget);
  if (shouldFallbackToVisible) {
    for (const clip of clips) {
      if (!clip.elementId) continue;
      if (isPlayheadWithinFxWindow(playhead, clip.start, clip.end))
        ids.add(clip.elementId);
    }
  }

  return ids;
}

// ── background-blur packet helpers ───────────────────────────────

export function packetIsBackgroundBlur(
  packet: Pick<FxRenderPacket, "effectType">,
): boolean {
  return packetEffectTypeIsBackgroundBlur(packet.effectType);
}

export function packetWantsSegmentation(
  packet: Pick<FxRenderPacket, "effectType" | "params">,
): boolean {
  if (!packetIsBackgroundBlur(packet)) return false;
  const fullScreen = Number(packet.params?.fullScreenBlur);
  if (Number.isFinite(fullScreen) && fullScreen > 0.5) return false;
  const enabled = Number(packet.params?.segmentationEnabled);
  // Segmentation-first default for real background blur.
  // Explicit opt-out: set params.segmentationEnabled to 0.
  if (!Number.isFinite(enabled)) return true;
  return enabled > 0.5;
}

/**
 * Deterministic overlap policy for background blur packets:
 * the last ordered background-blur packet wins for overlay composition.
 * Packet ordering is already stable via track/stack order in computeFxRenderPackets.
 */
export function selectBackgroundBlurPacketForOverlay(
  packets: FxRenderPacket[] | undefined,
): FxRenderPacket | null {
  if (!packets?.length) return null;
  let winner: FxRenderPacket | null = null;
  for (const packet of packets) {
    if (!packetIsBackgroundBlur(packet)) continue;
    winner = packet;
  }
  return winner;
}
